import { type FormEvent, useState } from 'react';
import { useReveal } from '../hooks/useReveal.js';
import { apiFetch } from '@/lib/api.js';

const MAX = 300;

/**
 * Anonymous feedback. One textarea, one promise: nothing about the sender is
 * sent or stored - only the words. POSTs to /feedback (public, rate-limited).
 */
export function Feedback() {
  const ref = useReveal<HTMLDivElement>(0.25);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = message.trim();
    if (trimmed.length < 3 || status === 'sending') return;
    setStatus('sending');
    try {
      await apiFetch<{ message: string }>('/feedback', {
        method: 'POST',
        body: { message: trimmed },
      });
      setStatus('sent');
      setMessage('');
    } catch {
      setStatus('error');
    }
  }

  return (
    <section id="feedback" className="relative w-full" style={{ padding: '76px 20px' }}>
      <div ref={ref} className="ow-reveal mx-auto" style={{ maxWidth: 720 }}>
        <div className="text-center">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 11, letterSpacing: '0.32em' }}
          >
            § VIII - Your Word
          </p>
          <h2
            className="font-display text-primary mt-5"
            style={{
              fontSize: 'clamp(32px, 5vw, 56px)',
              fontWeight: 500,
              letterSpacing: '0.02em',
            }}
          >
            Speak plainly.
          </h2>
          <p
            className="text-secondary mt-4 mx-auto"
            style={{ fontSize: 16, lineHeight: 1.7, maxWidth: 520 }}
          >
            Criticism, correction, a suggestion, leave it here. It is anonymous: no name, no
            account, nothing kept but the words.
          </p>
        </div>

        {status === 'sent' ? (
          <p
            className="scripture text-primary text-center mt-10"
            style={{ fontSize: 20, lineHeight: 1.6 }}
          >
            Received. Thank you for your honesty.
          </p>
        ) : (
          <form onSubmit={onSubmit} className="mt-10">
            <label htmlFor="feedback-message" className="sr-only">
              Your feedback
            </label>
            <textarea
              id="feedback-message"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value.slice(0, MAX));
                if (status === 'error') setStatus('idle');
              }}
              rows={4}
              maxLength={MAX}
              placeholder="What should be better?"
              className="ow-focusable w-full resize-none rounded-md text-primary"
              style={{
                background: 'var(--ow-bg-elevated)',
                border: '1px solid var(--ow-border)',
                padding: '14px 16px',
                fontSize: 15,
                lineHeight: 1.6,
              }}
            />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <span className="text-muted" style={{ fontSize: 12 }}>
                {message.length} / {MAX}
              </span>
              <div className="flex items-center gap-4">
                {status === 'error' && (
                  <span className="text-secondary" style={{ fontSize: 13 }}>
                    It could not be sent, please try again shortly.
                  </span>
                )}
                <button
                  type="submit"
                  className="ow-cta-primary"
                  disabled={message.trim().length < 3 || status === 'sending'}
                  style={
                    message.trim().length < 3 || status === 'sending'
                      ? { opacity: 0.5, cursor: 'default' }
                      : undefined
                  }
                >
                  {status === 'sending' ? 'Sending…' : 'Leave it here'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
